import React, { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Landmark, Plus, Trash2, Loader2, ShieldCheck, KeyRound } from "lucide-react";

export default function Banks() {
  const [banks, setBanks] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ bank_name: "", account_holder: "", account_number: "", routing_number: "", swift_code: "", nickname: "" });
  const [loading, setLoading] = useState(false);
  const [verifyId, setVerifyId] = useState(null);
  const [verifyCode, setVerifyCode] = useState("");

  const load = async () => {
    const { data } = await api.get("/wallet/banks");
    setBanks(data.banks || []);
  };
  useEffect(() => { load(); }, []);

  const addBank = async () => {
    if (!form.bank_name || !form.account_holder || !form.account_number) {
      toast.error("Bank name, holder, and account number are required.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/wallet/banks", form);
      toast.success("Bank added. Verify with the 4-digit code we sent (or use any 4-digit code for the demo).");
      setForm({ bank_name: "", account_holder: "", account_number: "", routing_number: "", swift_code: "", nickname: "" });
      setOpen(false);
      load();
      setVerifyId(data.id);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const verify = async () => {
    if (verifyCode.length !== 4) { toast.error("Enter the 4-digit code."); return; }
    try {
      await api.post(`/wallet/banks/${verifyId}/verify`, { code: verifyCode });
      toast.success("Bank verified.");
      setVerifyId(null); setVerifyCode("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteBank = async (id) => {
    if (!window.confirm("Remove this bank?")) return;
    try {
      await api.delete(`/wallet/banks/${id}`);
      setBanks(banks.filter((b) => b.id !== id));
      toast.success("Bank removed.");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <AppLayout>
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-eyebrow text-[#4F4D45]">Linked accounts</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1">External banks</h1>
          <p className="text-[#4F4D45] mt-2 max-w-xl">Link external bank accounts so live withdrawals settle faster. We verify ownership with a 4-digit code before any payout.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#1B3526] hover:bg-[#0F2517]" data-testid="banks-add-button">
              <Plus className="w-4 h-4 mr-2" /> Add bank
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>Add external bank</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <Label className="text-eyebrow text-[#4F4D45]">Bank name</Label>
                <Input value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} placeholder="Chase Bank" data-testid="banks-input-name" className="mt-1" />
              </div>
              <div>
                <Label className="text-eyebrow text-[#4F4D45]">Account holder</Label>
                <Input value={form.account_holder} onChange={(e) => setForm({ ...form, account_holder: e.target.value })} placeholder="Jordan Rivera" data-testid="banks-input-holder" className="mt-1" />
              </div>
              <div>
                <Label className="text-eyebrow text-[#4F4D45]">Account number</Label>
                <Input value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value.replace(/\s/g, "") })} placeholder="00012345678" data-testid="banks-input-acc" className="mt-1 font-mono" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-eyebrow text-[#4F4D45]">Routing #</Label>
                  <Input value={form.routing_number} onChange={(e) => setForm({ ...form, routing_number: e.target.value })} placeholder="Optional" data-testid="banks-input-routing" className="mt-1" />
                </div>
                <div>
                  <Label className="text-eyebrow text-[#4F4D45]">SWIFT</Label>
                  <Input value={form.swift_code} onChange={(e) => setForm({ ...form, swift_code: e.target.value })} placeholder="Optional" data-testid="banks-input-swift" className="mt-1" />
                </div>
              </div>
              <div>
                <Label className="text-eyebrow text-[#4F4D45]">Nickname</Label>
                <Input value={form.nickname} onChange={(e) => setForm({ ...form, nickname: e.target.value })} placeholder="Optional" data-testid="banks-input-nick" className="mt-1" />
              </div>
              <Button onClick={addBank} disabled={loading} className="bg-[#1B3526] hover:bg-[#0F2517] w-full" data-testid="banks-save-button">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save & request verification"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Pending verification card */}
      {verifyId && (
        <div className="mt-6 fv-card p-6 border-[#B58957]" data-testid="banks-verify-panel">
          <div className="flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-[#B58957]" />
            <h3 className="font-display text-lg">Verify the bank you just added</h3>
          </div>
          <p className="text-sm text-[#4F4D45] mt-2">For this demo, any 4-digit code completes verification. In production a micro-deposit confirms ownership.</p>
          <div className="mt-3 flex gap-2 max-w-sm">
            <Input value={verifyCode} onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, "").slice(0, 4))} placeholder="4-digit code" inputMode="numeric" className="font-mono text-xl tracking-[0.4em] text-center" data-testid="banks-verify-input" />
            <Button onClick={verify} className="bg-[#1B3526] hover:bg-[#0F2517]" data-testid="banks-verify-submit">Verify</Button>
          </div>
        </div>
      )}

      <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="banks-list">
        {banks.length === 0 && (
          <div className="col-span-full fv-card p-10 text-center">
            <Landmark className="w-10 h-10 text-[#4F4D45] mx-auto" strokeWidth={1.4} />
            <h3 className="font-display text-xl font-medium mt-4">No external banks yet</h3>
            <p className="text-[#4F4D45] text-sm mt-1">Link one to enable faster live withdrawals.</p>
          </div>
        )}
        {banks.map((b) => (
          <div key={b.id} className="fv-card p-6 relative" data-testid={`bank-item-${b.id}`}>
            <div className="flex items-start justify-between">
              <Landmark className="w-6 h-6 text-[#A85943]" strokeWidth={1.5} />
              <span className={`text-xs px-2 py-1 rounded-full ${b.verified ? "bg-[#264C39]/10 text-[#264C39]" : "bg-[#B58957]/20 text-[#8A4632]"} inline-flex items-center gap-1`}>
                {b.verified ? <><ShieldCheck className="w-3 h-3" /> Verified</> : "Unverified"}
              </span>
            </div>
            <div className="mt-5 font-display text-lg">{b.nickname || b.bank_name}</div>
            <div className="text-sm text-[#4F4D45]">{b.account_holder}</div>
            <div className="mt-2 font-mono text-sm">**** {b.last4}</div>
            {!b.verified && (
              <Button onClick={() => { setVerifyId(b.id); setVerifyCode(""); }} variant="outline" className="mt-4 w-full border-[#D2CDBE]" data-testid={`bank-verify-${b.id}`}>
                <KeyRound className="w-4 h-4 mr-2" /> Verify
              </Button>
            )}
            <button onClick={() => deleteBank(b.id)} data-testid={`bank-delete-${b.id}`} className="absolute top-3 right-3 text-[#4F4D45] hover:text-[#992F2F] p-1.5 rounded-md hover:bg-white/10">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </AppLayout>
  );
}
